<template>
  <div class="inappropriate-behavior-manager__container">
    <div class="inappropriate-behavior-manager__container__title rounded-top">
      <p class="m-0">Conducta Inapropiada</p>
    </div>

    <div v-if="isEnabledAssistantServiceNeedGuide" class="mx-4 mt-4">
      <CustomDropDown
        :disabled="!isEditable"
        label="Conducta inapropiada:"
        label-class="input-label font-14"
        v-model="studentBehaviour"
        placeholder="Seleccione"
        fieldkey="id"
        fieldname="name"
        :options="studentBehaviourOptions"
      />
    </div>

    <div v-for="(item, index) in inappropriateBehaviors" :key="index">
      <InappropriateBehaviorCollapsable
        :value="item"
        :is-editable="isEditable"
        :is-open="item.id == collapseOpenId"
        :behavior-type-options="behaviorTypeOptions"
        :behavioral-function-options="behavioralFunctionOptions"
        :behavioral-plan-options="behavioralPlanOptions"
        @remove="removeInappropriateBehavior"
        @open="collapseOpenId = item.id"
        @close="collapseOpenId = null"
        @update="updateInappropriateBehavior"
      />
    </div>

    <div class="px-4" v-if="showCreation">
      <div class="tag-badge my-4">
        <span class="dot text-center">
          <i class="fas fa-clipboard-user orange-icon" />
        </span>
        <span class="ml-2 bold">Nueva Conducta Inapropiada</span>
      </div>

      <InappropriateBehaviorForm
        v-model="inappropriateBehavior"
        ref="newInappropriateBehavior"
        :behavior-type-options="behaviorTypeOptions"
        :behavioral-function-options="behavioralFunctionOptions"
        :behavioral-plan-options="behavioralPlanOptions"
      />

      <div style="padding: 0 20px;">
        <div class="inappropriate-behavior-manager__container__actions">
          <ActionButton
            type="button"
            text="Cancelar"
            i="fa fa-times-circle"
            variant="outline-danger"
            class="px-4 mr-3"
            @click="cancelCreation"
          />

          <ActionButton
            type="button"
            text="Guardar"
            i="fas fa-save"
            variant="success"
            class="px-4"
            @click="addInappropriateBehavior"
          />
        </div>
      </div>
    </div>

    <div
      v-if="isEditable && !showCreation"
      class="m-4 d-flex justify-content-center"
    >
      <button
        @click="showAddInappropriateBehavior"
        class="btn bg-light-white"
        :style="
          `min-width: ${inappropriateBehaviors.length > 0 ? '100%' : '350px'}`
        "
      >
        <i class="fas fa-file-plus text-green pr-2 fa-lg" />
        Añadir Conducta Inapropiada
      </button>
    </div>
  </div>
</template>

<script>
import store from '@/store/index';
import ActionButton from 'ActionButton';
import mixAlert from '@/mixin/alert.mixin';
import CustomDropDown from 'custom-drop-down';
import InappropriateBehaviorCollapsable from './Form/Collapsable.vue';
import InappropriateBehaviorForm from './Form/Form.vue';
import { GetDefaultInappropriateBehavior } from './Form/Form.vue';
import { StudentInappropiateBehaviourOptions } from '@/components/pei/tabs/components/tab3/enum/StudentInappropiateBehaviourOptions';

import { ValidationObserver } from 'vee-validate';
import objectToFormData from 'object-to-formdata';

export default {
  name: 'InappropriateBehaviorManagerV2',
  mixins: [mixAlert],
  components: {
    ActionButton,
    CustomDropDown,
    ValidationObserver,
    InappropriateBehaviorForm,
    InappropriateBehaviorCollapsable,
  },
  props: {
    isEditable: {
      type: Boolean,
      required: true,
      default: true,
    },
  },
  data() {
    return {
      isInitialized: false,
      showCreation: false,
      collapseOpenId: null,
      inappropriateBehavior: GetDefaultInappropriateBehavior(),
      studentBehaviour: null,
      inappropriateBehaviors: [],
      behaviorTypeOptions: [],
      behavioralFunctionOptions: [],
      behavioralPlanOptions: [],
      timeoutStudentBehaviour: null,
      isEnabledAssistantServiceNeedGuide: false,
      studentBehaviourOptions: StudentInappropiateBehaviourOptions,
    };
  },
  methods: {
    async initData() {
      const { data } = await this.$ApiGet(
        `api/pei/${store.getters.SelectedPei}/inappropriatebehavior/init`
      );

      this.isEnabledAssistantServiceNeedGuide =
        data.isEnabledAssistantServiceNeedGuide;
      this.studentBehaviour = this.studentBehaviourOptions.find(
        (x) => x.id == data.studentBehaviourId
      );

      this.inappropriateBehaviors = data.inappropriateBehaviors;
      this.behaviorTypeOptions = data.peiBehaviorTypes;
      this.behavioralFunctionOptions = data.peiBehavioralFunctions;
      this.behavioralPlanOptions = data.peiBehavioralPlans.map((x) => ({
        value: x.id,
        label: x.name,
      }));
    },
    async fetchInappropriateBehaviors() {
      const { data } = await this.$ApiGet(
        `api/pei/${store.getters.SelectedPei}/inappropriatebehavior/list`
      );
      this.inappropriateBehaviors = data;
      this.collapseOpenId = null;
    },
    async removeInappropriateBehavior(itemId) {
      await this.$ApiDelete(
        `api/pei/${store.getters.SelectedPei}/inappropriatebehavior/${itemId}`
      );

      this.collapseOpenId = null;
      this.ShowToast(
        '',
        'Conducta Inapropiada eliminada exitosamente',
        'success'
      );

      await this.fetchInappropriateBehaviors();
    },
    cancelCreation() {
      this.showCreation = false;
      this.inappropriateBehavior = GetDefaultInappropriateBehavior();
    },
    showAddInappropriateBehavior() {
      this.showCreation = true;
      this.collapseOpenId = null;
    },
    async addInappropriateBehavior() {
      const isValid = await this.$refs.newInappropriateBehavior.validate();
      if (!isValid) return;

      const payload = objectToFormData(
        {
          peiId: store.getters.SelectedPei,
          typeId: this.inappropriateBehavior.behaviorType.id,
          functionOtherText: this.inappropriateBehavior
            .behavioralFunctionOtherText,
          triggerDescription: this.inappropriateBehavior.trigger,
          environmentDescription: this.inappropriateBehavior.environment,
          behavioralPlanId: this.inappropriateBehavior.behavioralPlan,
          behavioralPlanFile: this.inappropriateBehavior.behavioralPlanFile,
          functions: this.inappropriateBehavior.behavioralFunctions,
          strategies: this.inappropriateBehavior.strategies.map((x) => ({
            id: x.index,
            name: x.text,
          })),
          familySupports: this.inappropriateBehavior.familyCollaborations.map(
            (x) => ({ id: x.index, name: x.text })
          ),
        },
        { indices: true }
      );

      await this.$ApiPost(
        `api/pei/${store.getters.SelectedPei}/inappropriatebehavior`,
        payload
      );
      this.ShowToast(
        '',
        'Conducta Inapropiada agregada exitosamente',
        'success'
      );

      this.showCreation = false;
      this.inappropriateBehavior = GetDefaultInappropriateBehavior();
      await this.fetchInappropriateBehaviors();
    },
    async updateInappropriateBehavior(data) {
      const payload = objectToFormData(
        {
          id: data.id,
          peiId: store.getters.SelectedPei,
          typeId: data.behaviorType.id,
          functionOtherText: data.behavioralFunctionOtherText,
          triggerDescription: data.trigger,
          environmentDescription: data.environment,
          behavioralPlanId: parseInt(data.behavioralPlan),
          behavioralPlanFile: data.behavioralPlanFile,
          functions: data.behavioralFunctions.map((x) => parseInt(x)),
          strategies: data.strategies.map((x) => ({
            id: x.index,
            name: x.text,
          })),
          familySupports: data.familyCollaborations.map((x) => ({
            id: x.index,
            name: x.text,
          })),
        },
        { indices: true }
      );

      await this.$ApiPut(
        `api/pei/${store.getters.SelectedPei}/inappropriatebehavior`,
        payload
      );

      this.collapseOpenId = null;
      this.ShowToast(
        '',
        'Conducta Inapropiada actualizada exitosamente',
        'success'
      );

      await this.fetchInappropriateBehaviors();
    },
  },
  watch: {
    studentBehaviour(newValue, oldValue) {
      if (!this.isInitialized || newValue?.id == oldValue?.id) return;

      this.$emit('studentBehaviorChanged', newValue?.id);
      clearTimeout(this.timeoutStudentBehaviour);
      this.timeoutStudentBehaviour = setTimeout(async () => {
        await this.$ApiPost(
          `api/pei/${store.getters.SelectedPei}/updatestudentbehaviour${
            newValue?.id ? `?studentBehaviourId=${newValue?.id}` : ``
          }`
        );
        this.ShowToast(
          '',
          'Conducta Inapropiada actualizada exitosamente',
          'success'
        );
      }, 500);
    },
  },
  async mounted() {
    await this.initData();
    this.isInitialized = true;
  },
};
</script>

<style scoped>
.tag-badge {
  display: flex;
  align-items: center;
  background: linear-gradient(90deg, #ffffff 0%, #e8e8e8 100%);
  border-radius: 26px;
}

.dot {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0px 3px 6px #00000029;
  display: flex;
  align-items: center;
  justify-content: center;
}

.orange-icon {
  color: #ff7058;
  font-size: 16pt;
}

.inappropriate-behavior-manager__container {
  background-color: #fafbfa;
  padding-bottom: 10px;
}

.inappropriate-behavior-manager__container__title {
  background-color: #455a5f;
  text-align: center;
  color: #ffffff;
  padding: 15px 0;
}

.inappropriate-behavior-manager__container__actions {
  padding: 20px 0;
  display: flex;
  align-items: end;
  justify-content: end;
  border-top: 1px solid #e8e8e8;
}
</style>
